import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import PrimeVue from 'primevue/config';
import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import App from './components/App.vue';
import { scheduleKeycloakTokenRefresh, useKeycloak } from './composables/useKeycloak.ts';
import { setupSignalRConfig } from './composables/useSignalRConnection.ts';
import routes from './routes.ts';
import { setupHttpConfig } from './services/http.ts';
import { useValidationErrorsStore } from './stores/validationErrors.ts';
import * as Sentry from '@sentry/vue';

import Highcharts from 'highcharts';
import 'dayjs/locale/en';
import 'dayjs/locale/sv';
import 'dayjs/locale/nb';
import 'dayjs/locale/da';
import 'dayjs/locale/fi';
import 'highcharts/modules/accessibility';
import { SENTRY_DSN, SENTRY_ENVIRONMENT } from '@/config';

// Accessibility module draws its own focus border (default blue) instead of using
// the browser's native outline, so it must be recolored via Highcharts options.
Highcharts.setOptions({
    accessibility: {
        keyboardNavigation: {
            focusBorder: {
                style: {
                    color: '#474d58',
                },
            },
        },
    },
});

// Reload the page once on stale dynamic import errors (e.g. after a deploy)
window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message ?? '';
    const isImportError =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed');
    if (isImportError && !sessionStorage.getItem('chunk-reload')) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
    }
});
const keycloak = useKeycloak();

const isAuthenticated = () => {
    return keycloak.authenticated;
};

const goToLogin = () => {
    keycloak.login({
        redirectUri: window.location.href,
    });
};

keycloak.onTokenExpired = () => {
    keycloak.updateToken(0).catch(() => keycloak.login());
};

keycloak
    .init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
    })
    .then(async (authenticated: boolean) => {
        const router = createRouter({
            history: createWebHistory(),
            routes,
        });

        // If not authenticated, check if the current route is public.
        // If not, redirect to login without mounting the app (keeps the loader visible).
        if (!authenticated) {
            const resolved = router.resolve(window.location.pathname);
            if (!resolved.meta.public) {
                goToLogin();
                return;
            }
        }

        router.beforeEach((to, from) => {
            if (!to.meta.public && !isAuthenticated()) {
                goToLogin();
            }
        });

        router.onError((error, to) => {
            const isImportError =
                error.message.includes('Failed to fetch dynamically imported module') ||
                error.message.includes('Importing a module script failed');
            if (isImportError && !sessionStorage.getItem('chunk-reload')) {
                sessionStorage.setItem('chunk-reload', '1');
                window.location.assign(to.fullPath);
            }
        });

        router.afterEach(() => {
            useValidationErrorsStore().clearAll();
        });

        let userInfo: any = null;

        if (authenticated) {
            userInfo = await keycloak.loadUserInfo();

            scheduleKeycloakTokenRefresh(keycloak);

            const getToken = async () => {
                await keycloak.updateToken(60);

                if (!keycloak.token) throw new Error('No keycloak token available');

                return keycloak.token;
            };

            const tenantSlug = userInfo?.tenantName?.[0];
            if (tenantSlug) {
                setupHttpConfig(tenantSlug, getToken);
                setupSignalRConfig(() => tenantSlug, getToken);
            }
        }

        const pinia = createPinia();
        pinia.use(piniaPluginPersistedstate);

        const app = createApp(App);
        app.use(PrimeVue, { theme: { options: { cssLayer: true } } });
        app.use(pinia);
        app.use(router);
        const FIVE_MINUTES_IN_MILLISECONDS = 1000 * 60 * 5;
        app.use(VueQueryPlugin, {
            queryClientConfig: {
                defaultOptions: {
                    queries: {
                        retry: false,
                        retryOnMount: false,
                        refetchOnWindowFocus: false,
                        refetchOnReconnect: false,
                        staleTime: FIVE_MINUTES_IN_MILLISECONDS,
                    },
                    mutations: { retry: false },
                },
            },
        });

        app.config.globalProperties.$userInfo = userInfo;

        // Initialize Sentry only in production mode (npm run build)
        if (import.meta.env.MODE === 'production' && SENTRY_DSN) {
            Sentry.init({
                app,
                dsn: SENTRY_DSN,
                sendDefaultPii: false,
                environment: SENTRY_ENVIRONMENT,
                release: __APP_VERSION__,
            });
        }

        app.mount('#app');
        router.isReady().then(() => sessionStorage.removeItem('chunk-reload'));
    })
    .catch((error: Error) => {
        if (keycloak.authenticated) {
            console.error('Keycloak init failed after authentication', error);
            return;
        }

        goToLogin();
    });
